import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { WorkflowActionApiService } from '../services/workflow-action-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import {
  WorkflowActionTestResponse,
  WorkflowCommunicationActionType,
  ACTION_TEST_EXAMPLES
} from '../models/workflow-action.model';
import { EntitySelectComponent, EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowEmailTemplateApiService, EmailTemplateResponse } from '../services/workflow-email-template-api.service';
import { WorkflowRecipientListComponent } from '../shared/workflow-recipient-list.component';
import { workflowRecordLabel, workflowStatusLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-action-tester',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, EntitySelectComponent, WorkflowRecipientListComponent],
  templateUrl: './workflow-action-tester.component.html'
})
export class WorkflowActionTesterComponent implements OnInit, OnDestroy {
  actionType: WorkflowCommunicationActionType | string = 'SEND_INTERNAL_NOTIFICATION';
  objectType = 'CONTACT';
  loading = false;
  error = '';
  result: WorkflowActionTestResponse | null = null;

  // Campos amigables (inc 15) — el usuario no ve JSON
  // SEND_INTERNAL_NOTIFICATION
  notifRecipientType = 'OWNER';
  notifChannel = 'IN_APP';
  notifSubject = 'Nuevo lead';
  notifMessage = 'El contacto {{contact.firstname}} calificó.';
  // SEND_MARKETING_EMAIL
  emailTemplateId: number | null = null;
  emailSubject = 'Hola {{contact.firstname}}';
  emailProperty = 'email';
  respectSubscription = true;
  // SEND_ONE_TO_ONE_EMAIL
  otoeSubject = 'Seguimiento de tu cuenta';
  otoeBody = 'Hola {{contact.firstname}}, quería darte seguimiento...';
  otoeFromOwner = true;
  /** Correos sueltos extra para probar el "Para" combinado (además del contacto del registro). */
  otoeExtraEmailsRaw = '';
  // SEND_TEAMS_NOTIFICATION
  teamsChannelId = 'general';
  teamsMessage = 'Nuevo lead: {{contact.firstname}}';
  // Contexto de prueba (para render de tokens)
  demoContactName = 'Juan';

  // INC_CRM_020 (8) inc26-46 — mismo criterio que el inspector del constructor: el `value` es la
  // clave EXACTA que viaja al backend y el `label` es lo único que se traduce. Estas listas son
  // PROPIAS del probador (admite claves que el constructor no ofrece, p. ej. ASSIGNED_USER,
  // CREATOR o SUBSCRIPTION), por eso no se importan las del builder.
  readonly recipientTypeOptions: { value: string; label: string }[] = [
    { value: 'OWNER', label: 'Responsable del registro' },
    { value: 'ASSIGNED_USER', label: 'Usuario asignado' },
    { value: 'CREATOR', label: 'Quien creó el registro' }
  ];
  readonly channelOptions: { value: string; label: string }[] = [
    { value: 'IN_APP', label: 'Notificación dentro del CRM' },
    { value: 'EMAIL', label: 'Correo electrónico' }
  ];

  get isInternalNotif(): boolean { return this.actionType === 'SEND_INTERNAL_NOTIFICATION'; }
  get isMarketingEmail(): boolean { return this.actionType === 'SEND_MARKETING_EMAIL'; }
  get isOneToOneEmail(): boolean { return this.actionType === 'SEND_ONE_TO_ONE_EMAIL'; }
  get isTeamsNotif(): boolean { return this.actionType === 'SEND_TEAMS_NOTIFICATION'; }

  readonly actionTypeOptions: { value: WorkflowCommunicationActionType; label: string; enabled: boolean }[] = [
    { value: 'SEND_INTERNAL_NOTIFICATION', label: 'Notificación interna',   enabled: true },
    { value: 'SEND_MARKETING_EMAIL',        label: 'Correo de marketing',    enabled: true },
    { value: 'SEND_ONE_TO_ONE_EMAIL',       label: 'Correo 1 a 1',           enabled: true },
    { value: 'SEND_TEAMS_NOTIFICATION',     label: 'Notificación de Teams',  enabled: true },
    // La plantilla ya añade "(no disponible)" a las deshabilitadas: el label no repite el aviso.
    { value: 'SEND_SLACK_NOTIFICATION',     label: 'Slack',                  enabled: false },
    { value: 'SEND_SMS',                    label: 'SMS',                    enabled: false },
    { value: 'SEND_WHATSAPP',              label: 'WhatsApp',                enabled: false },
  ];

  readonly objectTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'COMPANY', label: 'Empresa' },
    { value: 'DEAL', label: 'Negocio' },
    { value: 'QUOTE', label: 'Cotización' },
    { value: 'TICKET', label: 'Ticket' }
  ];

  private destroy$ = new Subject<void>();

  // Selector de plantilla de email (EmailTemplate id numérico) — GAP 1
  private allEmailTemplates: EmailTemplateResponse[] = [];
  emailTemplatesAccessible = true;
  emailTemplateName = '';
  emailTemplateLabel = (t: EmailTemplateResponse): string => t.subject || t.purpose;
  emailTemplateSublabel = (t: EmailTemplateResponse): string =>
    `${t.purpose}${t.locale ? ' · ' + t.locale : ''} v${t.version}`;
  emailTemplateSearch = (term: string): Observable<EntitySelectPage<EmailTemplateResponse>> => {
    return this.emailTemplateApi.list().pipe(
      map((list) => {
        this.allEmailTemplates = list;
        const t = term.trim().toLowerCase();
        const items = t
          ? list.filter((x) => `${x.subject ?? ''} ${x.purpose ?? ''}`.toLowerCase().includes(t))
          : list;
        return { items, totalPages: 1 };
      }),
      catchError(() => { this.emailTemplatesAccessible = false; return of({ items: [] as EmailTemplateResponse[], totalPages: 0 }); })
    );
  };
  onEmailTemplateSelected(t: EmailTemplateResponse | null): void {
    this.emailTemplateName = t ? this.emailTemplateLabel(t) : '';
  }

  constructor(
    private actionApi: WorkflowActionApiService,
    private authService: AuthService,
    private emailTemplateApi: WorkflowEmailTemplateApiService
  ) {}

  ngOnInit(): void {
    this.onActionTypeChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onActionTypeChange(): void {
    this.result = null;
    this.error = '';
  }

  /** Construye el config JsonNode internamente desde los campos del formulario. */
  private buildCommConfig(): Record<string, any> {
    switch (this.actionType) {
      case 'SEND_INTERNAL_NOTIFICATION':
        return { recipientType: this.notifRecipientType, subject: this.notifSubject, message: this.notifMessage, channel: this.notifChannel, continueOnError: true };
      case 'SEND_MARKETING_EMAIL':
        // Forma canónica de destinatarios: en marketing solo el contacto del registro.
        return { emailTemplateId: this.emailTemplateId ?? undefined, subject: this.emailSubject, recipients: { includeRecordContact: true, emailProperty: this.emailProperty }, respectSubscription: this.respectSubscription, continueOnError: false };
      case 'SEND_ONE_TO_ONE_EMAIL':
        return { subject: this.otoeSubject, body: this.otoeBody, fromOwner: this.otoeFromOwner, recipients: { includeRecordContact: true, emailProperty: this.emailProperty, userIds: [], emails: this.otoeExtraEmails() }, continueOnError: false };
      case 'SEND_TEAMS_NOTIFICATION':
        return { teamChannelId: this.teamsChannelId, message: this.teamsMessage, continueOnError: true };
      default: {
        const ex = ACTION_TEST_EXAMPLES[this.actionType];
        return ex ? ex.config : {};
      }
    }
  }

  private buildContext(): Record<string, any> {
    return { contact: { firstname: this.demoContactName || 'Juan', email: 'demo@test.com' } };
  }

  test(): void {
    this.loading = true;
    this.error = '';
    this.result = null;

    this.actionApi.testAction({
      actionType: this.actionType,
      objectType: this.objectType,
      config: this.buildCommConfig(),
      context: this.buildContext()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.result = res; this.loading = false; },
      error: (err) => {
        const msg = err.error?.message ?? '';
        if (err.status === 403) this.error = 'No tienes permisos para realizar esta acción.';
        else if (msg.toLowerCase().includes('ticket')) this.error = 'No se pudo procesar la solicitud sobre el ticket.';
        else if (msg.toLowerCase().includes('sms') || msg.toLowerCase().includes('slack') || msg.toLowerCase().includes('whatsapp'))
          this.error = 'La integración no está configurada todavía.';
        else this.error = msg || 'Error al probar la acción.';
        this.loading = false;
      }
    });
  }

  /**
   * INC_CRM_020 (8) inc26-46 — el badge pintaba el enum crudo del backend (SUCCESS/FAILED/SKIPPED)
   * y la fila de detalle el `actionType` técnico. El valor que llega NO se toca; solo se traduce
   * lo que se muestra, y si aparece un estado/tipo desconocido se enseña tal cual.
   */
  /** Nombre del registro afectado: antes se pintaba «CONTACT #42». */
  readonly recordText = workflowRecordLabel;

  /**
   * Estado en español. El vocabulario vive en el diccionario único del módulo
   * (`models/workflow-labels.ts`); antes cada probador repetía su propio `switch` de tres casos y
   * cualquier otro estado del motor (`RETRY_SCHEDULED`, `PENDING`…) se colaba en crudo.
   */
  statusLabel(status: string | undefined): string {
    return workflowStatusLabel(status, 'Desconocido');
  }

  actionTypeLabel(type: string | undefined): string {
    if (!type) {
      return '—';
    }
    return this.actionTypeOptions.find((o) => o.value === type)?.label ?? type;
  }

  getStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-600';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }

  get resultOutputJson(): string {
    return JSON.stringify(this.result?.output ?? {}, null, 2);
  }

  /** Correos sueltos escritos en el probador (validados por el backend, que descarta los mal formados). */
  otoeExtraEmails(): string[] {
    return this.otoeExtraEmailsRaw.split(/[;,\s]+/).map((s) => s.trim()).filter(Boolean);
  }

  /** Lista detallada de destinatarios que devuelve la acción de email dentro de `output`. */
  get resultRecipients(): unknown {
    return this.result?.output?.['recipients'] ?? null;
  }
}
